import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, IconButton, Radio, RadioGroup, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { DocumentoForm, DocumentoSchema } from "../../../schemas/Funcionarios_cadastro/documento_schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Documento } from "../../../../domain/models/documento";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined, normalizeFileName } from "../../../../../../common/utils/utils";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * CadastroDocumentoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedDocumento: Documento | null,
  onSubmit: (form: DocumentoForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onDownload: (idFuncionarioArquivo: number, imageName: string) => void,
  onClose: () => void,
};

export const CadastroDocumentoModal = ({
  open,
  saving,
  selectedDocumento,
  onSubmit,
  onOpenChangeHistory,
  onDownload,
  onClose,
}: Props) => {
  const [documento, setDocumento] = useState(selectedDocumento);

  const hookForm = useForm<DocumentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DocumentoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedDocumento) {
        hookForm.setValue('statusArquivo', selectedDocumento.situacao);
        hookForm.setValue('nomeDocumento', selectedDocumento.descricao);
        hookForm.setValue('dataVencimento', selectedDocumento.dataVencimento);
        hookForm.setValue('dataEmissao', selectedDocumento.dataEmissao);
        hookForm.setValue('file', null);
        hookForm.setValue('limparArquivos', false);
      } else {
        hookForm.reset({ statusArquivo: 1 });
      }
      setDocumento(selectedDocumento ?? null);
    }
  }, [
    open,
    selectedDocumento,
    hookForm,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleDownload = useCallback(() => {
    if (!saving && documento) onDownload(documento.idDocumento, String(documento.nome));
  }, [saving, documento, onDownload]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        hookForm.setValue('limparArquivos', true);
        setDocumento(null);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      hookForm.setValue('limparArquivos', false);
      onChange(e.target.files.item(0));
    }
  }, [
    saving,
    hookForm,
  ]);

  const handleSubmit = useCallback((form: DocumentoForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedDocumento) {
      onOpenChangeHistory(selectedDocumento.idDocumento, LogTipoTabela.FUNCIONARIO_ARQUIVO);
    }
  }, [selectedDocumento, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar documento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <Controller
                name='statusArquivo'
                control={hookForm.control}
                render={({ field }) => (
                  <FormControl size="small" className="grid-col-1" required={true}>
                    <FormLabel id="statusArquivo" required={true}>
                      Status do Arquivo
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="statusArquivo"
                      name="statusArquivo"
                      row={true}
                      value={field.value ?? null}
                      onChange={(e) => { if (!saving) field.onChange(e); }}>

                      <FormControlLabel
                        className="text-black dark:text-white"
                        value={1}
                        control={<Radio />}
                        label="Atual" />

                      <FormControlLabel
                        className="text-black dark:text-white"
                        value={2}
                        control={<Radio />}
                        label="Obsoleto" />

                    </RadioGroup>
                  </FormControl>
                )}
              />


              <DefaultFormInput
                name={'nomeDocumento'}
                label={"Nome do Documento"}
                readOnly={saving}
                required={true}
                reactHookForm={hookForm}
              />

              {/* <Controller
                name='dataVencimento'
                control={hookForm.control}
                render={({ field }) => (
                  <FormDatePickerInput
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    label="Data de Vencimento"
                    value={field.value ? dayjs(field.value as any).startOf('day').utc() : null}
                    onChange={(e) => field.onChange(e?.toDate())} />
                )} /> */}

              <FormDatePickerInput
                name='dataVencimento'
                label="Data de Vencimento"
                reactHookForm={hookForm}
              />

              <FormDatePickerInput
                name='dataEmissao'
                label="Data de Emissão do Documento"
                reactHookForm={hookForm}
              />

              <div className="flex w-full">
                <Controller
                  name='file'
                  control={hookForm.control}
                  render={({ field }) => (documento && documento.nome && documento.nome.length > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span
                        className="cursor-pointer"
                        onMouseDown={handleDownload}>
                        {documento.nome && documento.nome.length > 40
                          ? `${documento.nome.substring(0, 40)}...`
                          : documento.nome}
                      </span>
                      <IconButton onClick={handleDeleteFile}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo"}
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(e) => handleFileChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedDocumento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}